// Unified Composer (Sprint UI-9 — rebuilt on shadcn primitives;
// DS.6 `aos-ds-composer` — rebuilt again on the As Oasis design system).
//
// Two modes:
//   mode='new'      — full-page surface for creating a new agent.
//   mode='continue' — bottom-bar surface for sending follow-ups.
//
// DS.6 (campaign aos-design-system, spec §2 D2/D3/D5/D6) — the AI Elements
// composer chrome is GONE, with the 1,269-line vendored template it came from
// and the seven shadcn `ui/` files only that template still held up (all nine
// deleted in the same commit, each on a re-measured last-importer verdict).
// What replaced it, element for element, is `approved-composer.png` (approved
// by Santi 2026-09-13): the card, the field frame and the chip row are plain
// layout on As Oasis SKIN ROLES (`--surface-*`, `--border-*`, `--radius-*`,
// `--space-*`), the field is `ds/DsComposerTextarea` over
// `@ds/data-input/Textarea`, the two selector chips are real
// `@ds/data-display/Chip` buttons through `ds/DsChipMenu` / `ds/DsChipPopover`,
// the project chip is a plain DS `Chip`, and every icon button is
// `ds/DsIconButton`. ADR D-026 §3 named this chrome AI-Elements-governed; the
// dated amendment in its §5 records that DS.6 moved it to the skin. The chat
// TIMELINE is untouched (DS.8) and so is every input BEHAVIOUR, which stays
// Cursor-mirrored (D-026 §3, `cursor-ux-mirror.md` §2.1).
//
// CHIP-ROW ALIGNMENT IS A FOUNDER-VISIBLE CONTRACT, not styling. Santi's
// 2026-09-13 reply to the mockup round: «la fila donde está Opus 4 con 8 y 38%
// abajo en el composer está desalineada». Root cause, measured: `index.css`
// floors every `[role="button"]` at the 44 px touch target with zero
// specificity, so a 20 px chip sat pinned to the top of a 44 px box while the
// donut and project chip centred. Every chip-zone item therefore sits in the
// same `--target-min`-tall, centre-aligned `ToolbarItem` box, and the DS chips
// carry the inline `minHeight: 0` that keeps them 20 px inside it.
//
// Shared across both modes:
//   • ds/DsComposerTextarea (auto-growing, capped AND scrollable, IME-safe)
//   • AutocompleteDropdown (Sprint 6.7)
//   • Visual viewport tracking (iOS keyboard offset)
//   • Enter to send (desktop), Shift+Enter for newline; on coarse-pointer
//     devices (mobile/tablet) Enter inserts a newline and the Send button is
//     the only way to submit
//
// Mode-specific:
//   'new'      — composerDraft from store, createAgent on send, char counter,
//                memory-constrained warning, Mode+Model+Effort chips.
//   'continue' — localStorage draft persistence, agent-status-aware placeholder,
//                Stop button, Mode+Model+Effort chips + ContextDonut,
//                unrecoverable banner, permission-change confirmation dialog.

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { Agent, EffortLevel, PermissionMode } from '../../types';
import { useVisualViewport } from '../../hooks/useVisualViewport';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useAppStore } from '../../stores/app.store';
import { useAutocomplete } from '../../hooks/useAutocomplete';
import { useTranslation } from '../../i18n';
import { AutocompleteDropdown } from '../AutocompleteDropdown';
import { ModelEffortChips } from '../ModelEffortChips';
import { ModeChip } from '../ModeChip';
import { ConfirmDialog } from '../ConfirmDialog';
import { ContextDonut } from '../ContextDonut';
import { ProjectChip } from '../ProjectChip';
import { Icon } from '../atoms/Icon';
import { ApiHttpError, createAgent, patchAgentConfig } from '../../lib/api';
import { useActiveWorkspaceStore } from '../../stores/active-workspace.store';
import { PROMPT_MAX_CHARS, PROMPT_WARN_CHARS, reconcileEffort } from '../../lib/models';
import {
  computeInputBlocked,
  placeholderForStatus,
} from '../../lib/agent-input';
import {
  isPermissionPending,
  isPermissionTerminal,
  isAskUserQuestionPending,
  getRequestId,
} from '../../lib/permissions/types';
import { DsComposerTextarea } from '../ds/DsComposerTextarea';
import { DsIconButton } from '../ds/DsIconButton';
import { cn } from '../../lib/utils';
import { AttachmentChip } from '../AttachmentChip';
import {
  admitFile,
  checkImageDimensions,
  emptyPolicyState,
  filesFromClipboard,
  filesFromDataTransfer,
  type AttachmentChipModel,
  type ChipPolicyState,
  type ChipRoute,
} from '../../lib/attachments';
import { uploadAttachment } from '../../lib/api';
import {
  useChipsForAgent,
  useComposerAttachmentsStore,
} from '../../stores/composer-attachments.store';
import {
  chipToPersisted,
  clearAgentDraft,
  hydrateChip,
  loadAgentDraft,
  saveAgentDraft,
} from '../../lib/composer-attachments-idb';

// ── Layout constants ──────────────────────────────────────────────────

const CONTINUE_MAX_LINES = 6;
const NEW_MIN_LINES = 4;
const NEW_MAX_LINES = 14;
const LINE_HEIGHT_PX = 24;
// `PADDING_PX` is gone with the shadcn textarea that owned it: the DS piece
// renders `padding: 0` on the real `<textarea>` (Textarea.module.css:78) and
// the field WRAPPER carries the padding (`fieldStyle`), exactly as
// `approved-composer.png` does. The min/max heights the adapter computes are
// therefore text heights — lines x line-height — and nothing double-counts.

// ── Chrome (skin roles only — `approved-composer.png` is the authority) ──

const cardStyle: CSSProperties = {
  background: 'var(--surface-secondary)',
  border: 'var(--border-width-default) solid var(--border-strong)',
  borderRadius: 'var(--radius-m)',
  display: 'flex',
  flexDirection: 'column',
};

const fieldStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  // Vertical padding is symmetric and the toolbar row below carries none: the
  // `--target-min`-tall `ToolbarItem` boxes already give the chip row its own
  // breathing room, and this distribution reproduces `approved-composer.png`'s
  // card while keeping the composer's RENDERED HEIGHT identical to the pre-DS.6
  // screen — which is what keeps `dashboard-conversation{,-expanded}` (clipped
  // at the composer's top edge) from moving a byte.
  padding: 'var(--space-2) var(--space-3)',
};

const toolbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-2)',
  padding: '0 var(--space-2)',
};

const chipRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  minWidth: 0,
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  flexShrink: 0,
};

// One box per chip-zone item, all `--target-min` tall and centre-aligned, so
// the four glyphs share ONE centre line (the founder's 2026-09-13 note) and the
// row keeps a real hit target. `ds6-composer.spec.ts` measures the four centres
// and asserts they agree within 1 px.
const toolbarItemStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 'var(--target-min)',
  flexShrink: 0,
};

// `shrink` is opt-in and today only the project chip asks for it: its label is
// the only one whose length is data (a repo slug), so it is the only item that
// can overflow the card. A flex item does not shrink unless BOTH `flexShrink`
// and `minWidth: 0` allow it — with the default `flexShrink: 0` the piece's own
// `text-overflow: ellipsis` can never fire (reviewer, 2026-09-14: an 85-char
// slug rendered a 534 px chip whose right edge escaped the composer card).
// The four centres are unaffected: shrinking changes width, never the centre
// line, and `ds6-composer.spec.ts` re-measures both.
function ToolbarItem({
  testId,
  children,
  shrink = false,
}: {
  testId: string;
  children: ReactNode;
  shrink?: boolean;
}) {
  return (
    <span
      data-testid={testId}
      style={shrink ? { ...toolbarItemStyle, flexShrink: 1, minWidth: 0 } : toolbarItemStyle}
    >
      {children}
    </span>
  );
}

// The new mode's Send is the one TEXT button on this surface, so it is a
// product block on skin roles rather than a DS piece: `@ds/actions/Button`
// ships a Material ripple on mousedown and this sprint renders no other text
// button (spec R2 B5 dropped the piece from DS.6 entirely).
function submitStyle(enabled: boolean): CSSProperties {
  return {
    background: 'var(--action-primary-bg)',
    color: 'var(--action-primary-text)',
    border: 'none',
    borderRadius: 'var(--radius-full)',
    padding: 'var(--space-2) var(--space-4)',
    fontFamily: 'var(--font-family-body)',
    fontSize: 'var(--type-label-lg)',
    lineHeight: 'var(--type-label-lg-lh)',
    fontWeight: 'var(--font-weight-medium)' as unknown as number,
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.5,
    minHeight: 0,
    minWidth: 0,
    flexShrink: 0,
  };
}

const attachmentsRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3) var(--space-1)',
};

// ── Draft persistence (continue mode) ─────────────────────────────────

const DRAFT_KEY_PREFIX = 'claude-dashboard:draft:';
const DRAFT_MAX_BYTES = 100_000;

function readDraft(agentId: string): string {
  if (typeof window === 'undefined') return '';
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY_PREFIX + agentId);
    if (raw === null) return '';
    if (raw.length > DRAFT_MAX_BYTES) return raw.slice(0, DRAFT_MAX_BYTES);
    return raw;
  } catch {
    return '';
  }
}

function writeDraft(agentId: string, text: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (text.length === 0) {
      window.localStorage.removeItem(DRAFT_KEY_PREFIX + agentId);
      return;
    }
    const capped =
      text.length > DRAFT_MAX_BYTES ? text.slice(0, DRAFT_MAX_BYTES) : text;
    window.localStorage.setItem(DRAFT_KEY_PREFIX + agentId, capped);
  } catch {
    // localStorage quota or disabled — ignore.
  }
}

// ── Props ─────────────────────────────────────────────────────────────

export type ComposerProps = ComposerNewProps | ComposerContinueProps;

export interface ComposerNewProps {
  mode: 'new';
  onOpenSidebar?: () => void;
}

export interface ComposerContinueProps {
  mode: 'continue';
  agent: Agent;
  // Sprint 8 — `chipIds` carries the ids of attachments uploaded via
  // POST /api/agents/:id/attachments. Empty/undefined → text-only send.
  onSend: (text: string, chipIds?: string[]) => Promise<void> | void;
  onStop?: () => Promise<void> | void;
  disableViewportTracking?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────

export function Composer(props: ComposerProps) {
  if (props.mode === 'new') {
    return <NewComposer {...props} />;
  }
  return <ContinueComposer {...props} />;
}

// ═══════════════════════════════════════════════════════════════════════
// mode='new' — full-page new-agent surface
// ═══════════════════════════════════════════════════════════════════════

function NewComposer({ onOpenSidebar }: ComposerNewProps) {
  const { t } = useTranslation();
  const draft = useAppStore((s) => s.composerDraft);
  const setComposerPrompt = useAppStore((s) => s.setComposerPrompt);
  const setComposerModel = useAppStore((s) => s.setComposerModel);
  const setComposerEffort = useAppStore((s) => s.setComposerEffort);
  const setComposerPermissionMode = useAppStore((s) => s.setComposerPermissionMode);
  const closeComposer = useAppStore((s) => s.closeComposer);
  const upsertAgent = useAppStore((s) => s.upsertAgent);
  const selectAgent = useAppStore((s) => s.selectAgent);
  const memoryConstrained = useAppStore((s) => s.concurrency.memoryConstrained);

  const promptRef = useRef<HTMLTextAreaElement>(null);
  const promptId = useId();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { keyboardOffset } = useVisualViewport();
  // Touch-primary devices (mobile/tablet): Enter inserts a newline so a
  // founder writing a multi-line prompt by thumb does not accidentally send
  // mid-paragraph. Send is then exclusively driven by the button.
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');

  const autocomplete = useAutocomplete({
    textareaRef: promptRef,
    value: draft.prompt,
    setValue: setComposerPrompt,
    disabled: submitting,
  });

  const charCount = draft.prompt.length;
  const overWarn = charCount > PROMPT_WARN_CHARS;
  const overMax = charCount > PROMPT_MAX_CHARS;
  const trimmed = draft.prompt.trim();
  const canSubmit =
    !submitting && trimmed.length > 0 && !overMax && !memoryConstrained;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const agent = await createAgent({
        prompt: trimmed,
        model: draft.model,
        effort: draft.effort,
        permissionMode: draft.permissionMode,
        projectId: useActiveWorkspaceStore.getState().activeWorkspaceId ?? undefined,
      });
      upsertAgent(agent);
      selectAgent(agent.id);
      closeComposer();
    } catch (err) {
      if (err instanceof ApiHttpError) setError(err.message);
      else if (err instanceof Error) setError(err.message);
      else setError(t('composer.couldNotStart'));
    } finally {
      setSubmitting(false);
    }
  }

  const counterClass = overMax
    ? 'text-destructive font-medium'
    : overWarn
      ? 'text-yellow-500'
      : 'text-muted-foreground/60';

  return (
    <main
      className="flex flex-col h-full w-full bg-background overflow-hidden"
      data-testid="chat-composer"
    >
      <header
        className="flex items-center gap-2 px-2 md:px-4 h-14 border-b border-border bg-background shrink-0"
        data-testid="composer-header"
      >
        {onOpenSidebar && (
          <span className="md:hidden">
            <DsIconButton
              variant="standard"
              size="sm"
              onClick={onOpenSidebar}
              aria-label={t('composer.openSidebar')}
              data-testid="composer-open-sidebar"
            >
              <Icon name="Menu" size={20} aria-hidden="true" />
            </DsIconButton>
          </span>
        )}
        <h1 className="text-xs md:text-sm font-semibold text-foreground">
          {t('composer.heading')}
        </h1>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto flex items-start md:items-center justify-center px-3 md:px-6 py-6">
        <div className="w-full max-w-reading mx-auto space-y-5">
          {memoryConstrained && (
            <div
              role="alert"
              className="rounded-md border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-sm text-yellow-500"
              data-testid="composer-memory-warning"
            >
              {t('composer.memoryWarning')}
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="composer-error"
            >
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor={promptId} className="sr-only">
              {t('composer.promptLabel')}
            </label>
            <div style={cardStyle}>
              <div style={fieldStyle}>
                <DsComposerTextarea
                  ref={promptRef}
                  id={promptId}
                  value={draft.prompt}
                  onChange={(e) => setComposerPrompt(e.target.value)}
                  onKeyDownIntercept={autocomplete.handleKeyDown}
                  onSend={() => void handleSubmit()}
                  isCoarsePointer={isCoarsePointer}
                  minLines={NEW_MIN_LINES}
                  maxLines={NEW_MAX_LINES}
                  lineHeightPx={LINE_HEIGHT_PX}
                  enterKeyHint="enter"
                  placeholder={t('composer.placeholder.exampleHint')}
                  autoFocus
                  disabled={submitting}
                  data-testid="composer-textarea"
                />
                {autocomplete.trigger && (
                  <AutocompleteDropdown
                    trigger={autocomplete.trigger.char}
                    query={autocomplete.trigger.query}
                    results={autocomplete.results}
                    focusedIndex={autocomplete.focusedIndex}
                    loading={autocomplete.loading}
                    error={autocomplete.error}
                    onSelect={autocomplete.accept}
                    onHoverIndex={autocomplete.setFocusedIndex}
                    onClose={autocomplete.close}
                  />
                )}
              </div>
              <div style={toolbarStyle}>
                <div style={chipRowStyle}>
                  <ToolbarItem testId="composer-item-model">
                    <ModelEffortChips
                      model={draft.model}
                      effort={draft.effort}
                      onModelChange={setComposerModel}
                      onEffortChange={setComposerEffort}
                      disabled={submitting}
                      size="md"
                    />
                  </ToolbarItem>
                  <ToolbarItem testId="composer-item-mode">
                    <ModeChip
                      mode={draft.permissionMode}
                      onChange={setComposerPermissionMode}
                      disabled={submitting}
                      size="md"
                    />
                  </ToolbarItem>
                </div>
                <div style={actionsStyle}>
                  <span
                    className={`text-xs ${counterClass}`}
                    data-testid="composer-char-count"
                  >
                    {charCount.toLocaleString()} /{' '}
                    {PROMPT_MAX_CHARS.toLocaleString()}
                  </span>
                  <button
                    type="button"
                    onClick={() => void handleSubmit()}
                    disabled={!canSubmit}
                    data-testid="composer-submit"
                    style={submitStyle(canSubmit)}
                  >
                    {submitting ? t('composer.starting') : t('composer.send')}
                  </button>
                </div>
              </div>
            </div>
            {overMax && (
              <p
                className="text-xs text-destructive"
                data-testid="composer-too-long"
              >
                {t('composer.promptTooLong', { count: (charCount - PROMPT_MAX_CHARS).toLocaleString() })}
              </p>
            )}
            {overWarn && !overMax && (
              <p
                className="text-xs text-yellow-500"
                data-testid="composer-warn"
              >
                {t('composer.promptApproachingLimit')}
              </p>
            )}
            <p className="text-xs text-muted-foreground/60">
              {t('composer.enterToSend')}
            </p>
          </div>
        </div>
      </div>

      <div
        aria-hidden="true"
        style={{ height: keyboardOffset || 0 }}
        className="shrink-0"
      />
    </main>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// mode='continue' — bottom-bar follow-up surface
// ═══════════════════════════════════════════════════════════════════════

function ContinueComposer({
  agent,
  onSend,
  onStop,
  disableViewportTracking,
}: ComposerContinueProps) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => readDraft(agent.id));
  const [sending, setSending] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  // Mid-chat mode switch triggers respawn (the `claude` process is killed
  // and recreated with the new --permission-mode flag). The founder sees
  // a confirm dialog naming the consequence before the respawn happens.
  const [pendingModeChange, setPendingModeChange] = useState<PermissionMode | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const upsertAgent = useAppStore((s) => s.upsertAgent);

  // Sprint messages-section-fixes (D-msg-1) — Edit on a user message
  // injects the message text into this composer via the app store. We
  // subscribe here, consume the value once, push it into local `text`
  // state, and focus + scroll the textarea so the founder lands ready
  // to edit.
  const pendingInjection = useAppStore(
    (s) => s.pendingComposerInjection[agent.id] ?? null,
  );
  const consumeComposerInjection = useAppStore((s) => s.consumeComposerInjection);
  useEffect(() => {
    if (pendingInjection === null) return;
    const value = consumeComposerInjection(agent.id);
    if (value === null) return;
    setText(value);
    // Defer focus + caret-to-end so the textarea has the new value
    // committed by the time we adjust selection.
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(value.length, value.length);
      el.scrollIntoView({ block: 'end', behavior: 'smooth' });
    });
  }, [pendingInjection, agent.id, consumeComposerInjection]);

  // Sprint composer-draft-attachments-persist — chip state moved out of
  // component-local useState into a per-agent Zustand store so chips
  // survive agent switches. Uploaded chips also survive page reloads
  // via the IndexedDB layer below. Pending/failed chips are intentionally
  // not persisted (resuming an aborted upload on reload is more complex
  // than the founder benefit — see sprint spec §3).
  const chips = useChipsForAgent(agent.id);
  const storeSetChips = useComposerAttachmentsStore((s) => s.setChips);
  const storeUpdateChips = useComposerAttachmentsStore((s) => s.updateChips);
  const storeSetMessageId = useComposerAttachmentsStore((s) => s.setMessageId);
  const storeClearAgent = useComposerAttachmentsStore((s) => s.clearAgent);

  // Adapter that mirrors React's `useState` setter API so existing
  // upload/admit/remove call-sites keep their `setChips(prev => ...)`
  // shape unchanged. Functional updates route through the store's
  // updateChips (read-then-write transactional); array writes route
  // through setChips (replace).
  const setChips = useMemo(
    () =>
      (
        arg:
          | AttachmentChipModel[]
          | ((prev: AttachmentChipModel[]) => AttachmentChipModel[]),
      ) => {
        if (typeof arg === 'function') {
          storeUpdateChips(agent.id, arg);
        } else {
          storeSetChips(agent.id, arg);
        }
      },
    [agent.id, storeSetChips, storeUpdateChips],
  );

  // Per-agent draft messageId — lives in the store too so it survives
  // agent switches. Lazily initialised on first admit (deferred so a
  // hydrate from IDB can restore the persisted messageId without racing
  // a synchronous random-uuid on mount).
  const draftMessageId = useComposerAttachmentsStore(
    (s) => s.byAgent[agent.id]?.messageId ?? null,
  );
  function ensureDraftMessageId(): string {
    if (draftMessageId) return draftMessageId;
    const fresh = crypto.randomUUID();
    storeSetMessageId(agent.id, fresh);
    return fresh;
  }

  // Derived from `chips`. Avoids the "two removeChip calls in one tick read
  // a stale closure of chips" bug that surfaced in Gate 2 review — a
  // useState-on-policy variant required us to recompute on every chip
  // change, which is exactly what useMemo already does.
  const policyState = useMemo<ChipPolicyState>(() => {
    return chips.reduce<ChipPolicyState>((acc, c) => {
      const result = admitFile(c.file, acc, c.messageId, { newId: () => c.id });
      if ('rejected' in result) return acc;
      return result.nextState;
    }, emptyPolicyState());
  }, [chips]);
  const [draggingOver, setDraggingOver] = useState(false);

  // Sprint composer-draft-attachments-persist — IDB hydrate. On agent
  // switch (or first mount), if the store has no chips for this agent
  // yet, try to load a persisted draft from IndexedDB. Persisted chips
  // are reconstructed with their Blob → File so the existing
  // ATTACHMENT_NOT_FOUND recovery (silent re-upload) keeps working if
  // the backend's 24h orphan TTL elapsed. `hydrating` blocks Send while
  // the read is in flight to avoid the race where a user reloads, types
  // text fast, and submits before the chip has come back.
  const [hydrating, setHydrating] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setHydrating(true);
    const liveChips =
      useComposerAttachmentsStore.getState().byAgent[agent.id]?.chips ?? [];
    if (liveChips.length > 0) {
      // The store already holds chips for this agent (in-memory survived
      // an agent switch). Skip IDB — store wins.
      setHydrating(false);
      return;
    }
    void (async () => {
      const persisted = await loadAgentDraft(agent.id);
      if (cancelled) return;
      if (persisted && persisted.chips.length > 0) {
        const hydrated = persisted.chips.map(hydrateChip);
        storeSetChips(agent.id, hydrated);
        storeSetMessageId(agent.id, persisted.messageId);
      }
      setHydrating(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [agent.id, storeSetChips, storeSetMessageId]);

  // Sprint composer-draft-attachments-persist — IDB persist. Whenever
  // the agent's chips change, snapshot uploaded chips to IDB so they
  // survive page reload. Pending/failed are filtered out by
  // chipToPersisted. Debounced via the React effect's natural batching
  // — multiple setChips calls in one tick produce one effect run.
  useEffect(() => {
    if (hydrating) return;
    const persistedChips = chips
      .map(chipToPersisted)
      .filter((c): c is NonNullable<ReturnType<typeof chipToPersisted>> => c !== null);
    if (persistedChips.length === 0) {
      void clearAgentDraft(agent.id);
      return;
    }
    const messageId = draftMessageId ?? persistedChips[0]!.messageId;
    void saveAgentDraft(agent.id, {
      agentId: agent.id,
      messageId,
      chips: persistedChips,
    });
  }, [agent.id, chips, draftMessageId, hydrating]);

  // Cancel uploads on unload (ATT16). Unchanged from Sprint 8 — page
  // unload still aborts in-flight HTTP so the backend's multer/busboy
  // tears down its temp file. Agent switches no longer abort uploads
  // (the previous ATT17 effect is removed; uploads continue in the
  // background and the user finds completed chips on return).
  useEffect(() => {
    const onBeforeUnload = () => {
      for (const c of chips) c.abortController?.abort();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [chips]);

  const messages = useAppStore((s) => s.messagesByAgent[agent.id]);
  const connectionStatus = useAppStore((s) => s.connectionStatus);
  const { keyboardOffset } = useVisualViewport();
  const offset = disableViewportTracking ? 0 : keyboardOffset;
  // Touch-primary devices (mobile/tablet): Enter inserts a newline so a
  // founder writing a multi-line prompt by thumb does not accidentally send
  // mid-paragraph. Send is then exclusively driven by the button.
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');

  // Sprint lazy-spawn-recovery (§2 D12) — `computeInputBlocked` always
  // returns 'none' now; the gate + its UnrecoverableBanner branch are
  // removed (every pre-init death is recoverable, §2 D7).
  const inputBlocked = computeInputBlocked(agent) !== 'none';

  const autocomplete = useAutocomplete({
    textareaRef,
    value: text,
    setValue: setText,
    disabled: inputBlocked || sending,
  });

  // Sprint working-signal-ssot (spec D6) — widened from `running` only:
  // a `stuck` agent is a live mid-turn process SIGTERM meaningfully
  // aborts (D-008 §2.2 "Stop is shown only while Working" + stuck now IS
  // Working). Deliberately NOT `agent.working`: the awaiting-task /
  // awaiting-wakeup gaps have no live turn to abort.
  const canStop = !!onStop && (agent.status === 'running' || agent.status === 'stuck');

  const textOwnerRef = useRef(agent.id);
  useEffect(() => {
    textOwnerRef.current = agent.id;
    setText(readDraft(agent.id));
  }, [agent.id]);

  // Cursor mirror — opening/switching a conversation leaves the caret in the
  // composer. Desktop only: on touch devices this would pop the keyboard on
  // every chat tap.
  useEffect(() => {
    if (isCoarsePointer) return;
    textareaRef.current?.focus();
  }, [agent.id, isCoarsePointer]);

  useEffect(() => {
    if (textOwnerRef.current !== agent.id) return;
    writeDraft(agent.id, text);
  }, [agent.id, text]);

  // Sprint 8 — chips count toward send-readiness. Send is allowed if there
  // is text OR at least one uploaded chip; pending chips block send (ATT14)
  // until they finish or the user removes them.
  //
  // Sprint composer-draft-attachments-persist — we do NOT gate canSend
  // on `hydrating`. The race window is one microtask (IDB read resolves
  // before the next paint), and a user who reloads-and-immediately-sends
  // would not have seen the chip rail render anyway. If they submit
  // text without the persisted chip, they get a normal send and can
  // re-attach + re-send. Pre-mortem failure mode #2 mitigation was
  // overzealous for the actual risk profile, and gating broke baseline
  // send tests that don't wait for the hydrate microtask.
  const hasUploadedChip = chips.some((c) => c.state === 'uploaded');
  const hasPendingChip = chips.some((c) => c.state === 'pending');
  const hasFailedChip = chips.some((c) => c.state === 'failed');
  const canSend =
    !sending &&
    !inputBlocked &&
    !hasPendingChip &&
    !hasFailedChip &&
    (text.trim().length > 0 || hasUploadedChip);

  // Upload a single chip, updating its state as it progresses. Emits to the
  // backend's POST /api/agents/:id/attachments. On error, surfaces a 'failed'
  // state with a retry affordance.
  async function uploadChip(chip: AttachmentChipModel): Promise<void> {
    const ctrl = new AbortController();
    setChips((prev) =>
      prev.map((c) =>
        c.id === chip.id ? { ...c, state: 'pending', progress: 0.1, abortController: ctrl, errorMessage: undefined } : c,
      ),
    );
    try {
      const resp = await uploadAttachment({
        agentId: agent.id,
        chipId: chip.id,
        messageId: chip.messageId,
        file: chip.file,
        signal: ctrl.signal,
      });
      setChips((prev) =>
        prev.map((c) =>
          c.id === chip.id
            ? {
                ...c,
                state: 'uploaded',
                progress: 1,
                serverRoute: resp.route as ChipRoute,
              }
            : c,
        ),
      );
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Subida falló';
      setChips((prev) =>
        prev.map((c) =>
          c.id === chip.id ? { ...c, state: 'failed', errorMessage: message } : c,
        ),
      );
    }
  }

  // Admit a list of files — applies the policy gate, kicks off uploads for
  // every accepted chip. Surfaces rejected files via the inline error line
  // so the founder sees a single message even on a multi-file drop.
  // Note: `policyState` is derived (useMemo) so we don't write it back; the
  // local `nextPolicy` accumulator only sequences the admit-checks for the
  // batch we're about to add.
  async function admitFiles(files: File[]) {
    if (files.length === 0) return;
    let nextPolicy = policyState;
    const accepted: AttachmentChipModel[] = [];
    const rejections: string[] = [];
    for (const f of files) {
      // Image dimension pre-flight: Anthropic vision rejects >8000×8000 px.
      // We decode the file just enough to read naturalWidth/Height before
      // policy gating so the founder sees a clean rejection inline rather
      // than an opaque API error after the upload completes.
      const mime = f.type || '';
      if (mime.startsWith('image/')) {
        const dim = await checkImageDimensions(f);
        if ('rejected' in dim) {
          rejections.push(`${f.name}: ${dim.rejected}`);
          continue;
        }
      }
      const result = admitFile(f, nextPolicy, ensureDraftMessageId(), {
        newId: () => crypto.randomUUID(),
      });
      if ('rejected' in result) {
        rejections.push(`${f.name}: ${result.rejected.reason}`);
        continue;
      }
      accepted.push(result.chip);
      nextPolicy = result.nextState;
    }
    if (accepted.length > 0) {
      setChips((prev) => [...prev, ...accepted]);
      for (const c of accepted) void uploadChip(c);
    }
    if (rejections.length > 0) {
      setError(rejections.join(' • '));
    } else {
      setError(null);
    }
  }

  function removeChip(id: string) {
    setChips((prev) => {
      const c = prev.find((x) => x.id === id);
      c?.abortController?.abort();
      return prev.filter((x) => x.id !== id);
    });
    // policyState is derived from chips via useMemo — it recomputes
    // automatically on the next render. No manual rebuild needed.
  }

  function retryChip(id: string) {
    const target = chips.find((c) => c.id === id);
    if (!target) return;
    void uploadChip(target);
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const files = filesFromClipboard(e.clipboardData?.items ?? null);
    if (files.length === 0) return; // plain-text paste falls through (ATT2)
    e.preventDefault();
    void admitFiles(files);
  }

  async function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDraggingOver(false);
    const dt = e.dataTransfer;
    const { files, rejectedFolderCount } = await filesFromDataTransfer(dt.items);
    if (rejectedFolderCount > 0) {
      setError('Suelta archivos individuales, no carpetas.');
    }
    void admitFiles(files);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      setDraggingOver(true);
    }
  }

  function handleDragLeave() {
    setDraggingOver(false);
  }

  function handlePickerChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    void admitFiles(files);
    // Reset so the same file can be picked again right after.
    e.target.value = '';
  }

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  // Translate the raw backend "Adjunto no encontrado o caducado: <UUID>"
  // wording into a founder-mode line. Singular if the draft has one chip,
  // plural otherwise. Strips the UUID, names the next action.
  function attachmentLostMessage(chipCount: number): string {
    return chipCount <= 1 ? t('composer.attachmentLost') : t('composer.attachmentsLost');
  }

  // Re-upload every chip that still has its File reference and retry the
  // send once. Returns ok=true if the retry succeeded; otherwise a reason
  // the caller maps to a founder-mode message. Silent — does not mutate
  // chip.state, so the UI does not flash 'pending'.
  async function recoverAttachmentsAndRetrySend(
    payload: string,
    chipsAtSendTime: AttachmentChipModel[],
  ): Promise<{ ok: true } | { ok: false }> {
    const recoverable = chipsAtSendTime.filter(
      (c) => c.state === 'uploaded' && c.file instanceof File,
    );
    if (recoverable.length === 0) return { ok: false };
    try {
      await Promise.all(
        recoverable.map((c) =>
          uploadAttachment({
            agentId: agent.id,
            chipId: c.id,
            messageId: c.messageId,
            file: c.file,
          }),
        ),
      );
    } catch {
      return { ok: false };
    }
    const chipIds = recoverable.map((c) => c.id);
    try {
      await onSend(payload, chipIds);
      return { ok: true };
    } catch {
      // Second failure (any reason). Caller surfaces founder-mode wording.
      return { ok: false };
    }
  }

  async function handleSend() {
    if (!canSend) return;
    const payload = text.trim();
    const uploadedChips = chips.filter((c) => c.state === 'uploaded');
    const chipIds = uploadedChips.map((c) => c.id);
    setSending(true);
    setError(null);
    try {
      // Conditional arity preserves the legacy onSend(text) signature for
      // call-sites and tests that haven't been migrated to the chips API.
      if (chipIds.length > 0) {
        await onSend(payload, chipIds);
      } else {
        await onSend(payload);
      }
      setText('');
      writeDraft(agent.id, '');
      storeClearAgent(agent.id);
      void clearAgentDraft(agent.id);
    } catch (err) {
      // ATTACHMENT_NOT_FOUND fires when the backend's in-memory store has
      // swept the chip (TTL > now-createdAt) or was restarted. The File
      // reference still lives in the browser, so we can re-upload silently
      // and retry the send once. If recovery fails (no File ref, second
      // ATTACHMENT_NOT_FOUND, or upload error), surface a founder-mode
      // line that names the next action — never the raw UUID.
      if (
        err instanceof ApiHttpError &&
        err.code === 'ATTACHMENT_NOT_FOUND' &&
        uploadedChips.length > 0
      ) {
        const recovery = await recoverAttachmentsAndRetrySend(payload, uploadedChips);
        if (recovery.ok) {
          setText('');
          writeDraft(agent.id, '');
          storeClearAgent(agent.id);
          void clearAgentDraft(agent.id);
        } else {
          setError(attachmentLostMessage(uploadedChips.length));
        }
      } else {
        setError(err instanceof Error ? err.message : t('composer.sendFailed'));
      }
    } finally {
      setSending(false);
    }
  }

  async function handleStop() {
    if (!canStop || stopping) return;
    setStopping(true);
    try {
      await onStop?.();
    } finally {
      setStopping(false);
    }
  }

  async function applyConfig(next: {
    model?: string;
    effort?: EffortLevel;
    permissionMode?: PermissionMode;
  }) {
    const optimistic: Agent = {
      ...agent,
      model: next.model ?? agent.model,
      effort: next.effort ?? agent.effort,
      permissionMode: next.permissionMode ?? agent.permissionMode ?? 'agent',
    };
    upsertAgent(optimistic);
    setConfigError(null);
    try {
      const updated = await patchAgentConfig(agent.id, next);
      upsertAgent(updated);
    } catch (err) {
      upsertAgent(agent);
      setConfigError(
        err instanceof Error ? err.message : t('composer.couldNotChangeAgent'),
      );
    }
  }

  function handleModelChange(nextModel: string) {
    const nextEffort = reconcileEffort(
      nextModel,
      agent.effort as EffortLevel,
    );
    void applyConfig({
      model: nextModel,
      effort: nextEffort !== agent.effort ? nextEffort : undefined,
    });
  }

  function handleEffortChange(nextEffort: EffortLevel) {
    void applyConfig({ effort: nextEffort });
  }

  function handleModeChange(nextMode: PermissionMode) {
    const currentMode = agent.permissionMode ?? 'agent';
    if (nextMode === currentMode) return;
    setPendingModeChange(nextMode);
  }

  function confirmModeChange() {
    const next = pendingModeChange;
    setPendingModeChange(null);
    if (next === null) return;
    void applyConfig({ permissionMode: next });
  }

  // D013-EC-197 (sprint e97-permission-window) D8, BLOCKER C13 — the
  // pre-sprint version walked backwards and returned true on the FIRST
  // message shaped as pending, with no per-request_id correlation between
  // a pending message and its own later terminal message. Once a
  // conversation had ever had more than one card, an OLDER card still
  // shaped as pending (its own terminal write carries neither `status:
  // 'pending'` nor a matching shape) kept the composer stuck on "esperando
  // tu decisión" forever, even over turns whose most recent card had long
  // since resolved.
  //
  // Fix: track, per request_id, whether we've already seen a MORE RECENT
  // message for that id (walking backwards, the first sighting of any id
  // IS its most recent status). A terminal message marks its id
  // "superseded" so an older pending sighting of the SAME id is skipped,
  // not treated as live.
  function hasPendingPermission(): boolean {
    const list = messages ?? [];
    const superseded = new Set<string>();
    for (let i = list.length - 1; i >= 0; i--) {
      const m = list[i];
      if (!m) continue;
      if (m.type === 'permission_request') {
        const id = getRequestId(m.content);
        if (isPermissionPending(m.content)) {
          if (id !== null && superseded.has(id)) continue;
          return true;
        }
        if (isPermissionTerminal(m.content)) {
          if (id !== null) superseded.add(id);
          continue;
        }
      }
      if (m.type === 'ask_user_question' && isAskUserQuestionPending(m.content)) {
        const id = getRequestId(m.content);
        if (id !== null && superseded.has(id)) continue;
        return true;
      }
    }
    return false;
  }

  const showStop = canStop;
  const sendEnabled = canSend;
  const buttonAction: 'send' | 'stop' | 'disabled' = showStop
    ? 'stop'
    : sendEnabled
      ? 'send'
      : 'disabled';

  return (
    <>
      <div
        className="border-t border-border bg-secondary px-3 md:px-4 pt-3 pb-3 md:pb-4 transition-[padding,transform] duration-150"
        style={{
          paddingBottom: `calc(0.75rem + env(safe-area-inset-bottom) + ${offset}px)`,
        }}
        data-testid="chat-input-container"
      >
       <div className="max-w-reading mx-auto">

        {configError && (
          <p
            className="mb-2 text-xs text-destructive"
            role="alert"
            data-testid="composer-config-error"
          >
            {configError}
          </p>
        )}

        <div
          className={cn('transition-colors duration-150')}
          style={{
            ...cardStyle,
            borderColor: draggingOver ? 'var(--border-brand)' : 'var(--border-strong)',
          }}
          data-testid="composer-card"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(e) => void handleDrop(e)}
        >
          {chips.length > 0 && (
            <div style={attachmentsRowStyle} data-testid="composer-chip-row">
              {chips.map((c) => (
                <AttachmentChip
                  key={c.id}
                  id={c.id}
                  route={c.predictedRoute}
                  name={c.displayName}
                  size={c.size}
                  state={c.state}
                  progress={c.progress}
                  errorMessage={c.errorMessage}
                  onRemove={removeChip}
                  onRetry={c.state === 'failed' ? retryChip : undefined}
                  agentId={agent.id}
                  localFile={c.file}
                />
              ))}
            </div>
          )}
          <div style={fieldStyle}>
            {/* The hidden picker lives inside the field wrapper so a click on
                the card cannot reach it; `sr-only` keeps it out of layout. */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,.md,.txt,.csv,.json,.xml,.html,.log,.ipynb,.yaml,.yml,text/*"
              className="sr-only"
              onChange={handlePickerChange}
              data-testid="composer-file-picker"
            />
            <DsComposerTextarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDownIntercept={autocomplete.handleKeyDown}
              onSend={() => void handleSend()}
              isCoarsePointer={isCoarsePointer}
              minLines={1}
              maxLines={CONTINUE_MAX_LINES}
              lineHeightPx={LINE_HEIGHT_PX}
              onPaste={handlePaste}
              enterKeyHint="enter"
              disabled={inputBlocked || sending}
              placeholder={placeholderForStatus({
                status: agent.status,
                hasSession: !!agent.claudeSessionId,
                isDisconnected: connectionStatus === 'disconnected',
                isArchived: agent.archived,
                hasPermissionPending: hasPendingPermission(),
                working: agent.working,
              })}
              data-testid="chat-textarea"
            />
            {autocomplete.trigger && (
              <AutocompleteDropdown
                trigger={autocomplete.trigger.char}
                query={autocomplete.trigger.query}
                results={autocomplete.results}
                focusedIndex={autocomplete.focusedIndex}
                loading={autocomplete.loading}
                error={autocomplete.error}
                onSelect={autocomplete.accept}
                onHoverIndex={autocomplete.setFocusedIndex}
                onClose={autocomplete.close}
              />
            )}
          </div>

          <div style={toolbarStyle} data-testid="composer-toolbar">
            <div style={chipRowStyle}>
              <ToolbarItem testId="composer-item-model">
                <ModelEffortChips
                  model={agent.model}
                  effort={agent.effort as EffortLevel}
                  onModelChange={handleModelChange}
                  onEffortChange={handleEffortChange}
                />
              </ToolbarItem>
              <ToolbarItem testId="composer-item-mode">
                <ModeChip
                  mode={agent.permissionMode ?? 'agent'}
                  onChange={handleModeChange}
                />
              </ToolbarItem>
              <ToolbarItem testId="composer-item-context">
              <ContextDonut
                pct={agent.contextUsedPct ?? null}
                // Sprint compaction-visibility (D14) — pass the EXISTING
                // `tooltip` prop when the real token counts are known.
                // Either null (never measured, OR just past a
                // compact_boundary that nulled `contextUsedTokens` — D8)
                // → no prop → ContextDonut's own generic default tooltip.
                tooltip={
                  agent.contextUsedTokens != null &&
                  agent.contextWindowTokens != null &&
                  agent.contextUsedPct != null
                    ? t('contextDonut.tooltipWithTokens', {
                        used: agent.contextUsedTokens.toLocaleString(),
                        max: agent.contextWindowTokens.toLocaleString(),
                        pct: Math.round(agent.contextUsedPct),
                      })
                    : undefined
                }
              />
              </ToolbarItem>
              <ToolbarItem testId="composer-item-project" shrink>
                <ProjectChip agent={agent} />
              </ToolbarItem>
            </div>
            <div style={actionsStyle}>
              {/* Desktop keeps drag-drop and paste as the attach paths, so the
                  paperclip stays `lg:hidden` — today's behaviour, unchanged by
                  DS.6. `approved-composer.png` SHOWS it only because DS.4's
                  story declares that deviation in its own header (the QA round
                  asked for the attach control to be judged). */}
              <span className="lg:hidden">
                <DsIconButton
                  variant="standard"
                  size="sm"
                  onClick={openFilePicker}
                  aria-label={t('composer.attachAriaLabel')}
                  data-testid="composer-attach-button"
                >
                  <Icon name="Paperclip" size={18} aria-hidden="true" />
                </DsIconButton>
              </span>
              <FusedSendStopButton
                action={buttonAction}
                sending={sending}
                stopping={stopping}
                onSend={() => void handleSend()}
                onStop={() => void handleStop()}
                sendLabel={t('composer.sendAriaLabel')}
                stopLabel={t('composer.stopAriaLabel')}
              />
            </div>
          </div>
        </div>
        {error && (
          <p
            className="mt-2 text-xs text-destructive"
            data-testid="chat-input-error"
            role="alert"
          >
            {error}
          </p>
        )}

       </div>

      </div>
      <ConfirmDialog
        open={pendingModeChange !== null}
        title={t('confirm.changeMode.title', {
          mode: pendingModeChange ? t(`modeChip.${pendingModeChange}` as const) : '',
        })}
        body={t('confirm.changeMode.body')}
        confirmLabel={t('confirm.changeMode.confirm')}
        onConfirm={confirmModeChange}
        onCancel={() => setPendingModeChange(null)}
      />
    </>
  );
}

// ── Fused Send/Stop button ─────────────────────────────────────────────
//
// Tri-state (send / stop / disabled) is ours and stays a PRODUCT BLOCK — it is
// the one place D-008 / D-028 semantics live: Stop is a first-class action and
// there is NO Resume / Retry / Continue affordance anywhere, because the
// composer IS the resume. DS.6 re-bases its chrome on `ds/DsIconButton`
// (`@ds/actions/IconButton` with the Material ripple suppressed), which renders
// a real `<button disabled>`, so every `toBeDisabled()` assertion on
// `chat-send-button` holds unchanged.
function FusedSendStopButton(props: {
  action: 'send' | 'stop' | 'disabled';
  sending: boolean;
  stopping: boolean;
  onSend: () => void;
  onStop: () => void;
  sendLabel: string;
  stopLabel: string;
}) {
  const { action, sending, stopping, onSend, onStop, sendLabel, stopLabel } = props;
  if (action === 'stop') {
    return (
      <DsIconButton
        variant="standard"
        size="sm"
        onClick={onStop}
        disabled={stopping}
        aria-label={stopLabel}
        data-testid="composer-stop-button"
      >
        <Icon name="Square" size={14} aria-hidden="true" />
      </DsIconButton>
    );
  }
  const disabled = action === 'disabled' || sending;
  return (
    <DsIconButton
      variant="standard"
      size="sm"
      onClick={onSend}
      disabled={disabled}
      aria-label={sendLabel}
      data-testid="chat-send-button"
    >
      <Icon name="ArrowUp" size={18} aria-hidden="true" />
    </DsIconButton>
  );
}

// Sprint lazy-spawn-recovery (§2 D12) — the `UnrecoverableBanner`
// component + its Fork-CTA test id were removed here. D-008 prescribes
// a "Create new" CTA and states Fork is removed from the user-visible
// surface; this banner shipped the forbidden Fork CTA in violation of
// its own ADR (challenge R4). After §2 D7 every pre-init
// death is recoverable on the next send, so the predicate that gated this
// banner (`error && !claudeSessionId`) can no longer match a case this
// sprint hasn't already made recoverable.
